import React, { useState } from 'react';
import { Modal, Upload, Button, Table, message, Alert, Space, Tag, Tabs } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import billService from '../../services/bill';

const { Dragger } = Upload;

const ImportModal = ({ visible, onCancel, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [skippedRecords, setSkippedRecords] = useState([]);
  const [selectedSkippedRows, setSelectedSkippedRows] = useState([]);

  const handleUpload = async (file) => {
    setLoading(true);
    try {
      const result = await billService.importBills(file);
      setParseResult(result.data);
      
      // 默认选中：非重复、金额不为0的有效记录
      const validIndexes = result.data.records
        .map((record, index) => ({ record, index }))
        .filter(({ record, index }) => {
          const isDuplicate = result.data.duplicates?.some(d => d.index === index);
          const isZeroAmount = record.amount === 0;
          return !isDuplicate && !isZeroAmount;
        })
        .map(({ index }) => index);
      setSelectedRows(validIndexes);
      
      // 保存跳过的无效记录信息
      setSkippedRecords(result.data.errors || []);
      
      setStep(2);
    } catch (error) {
      message.error(error.response?.data?.message || '文件解析失败');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleSave = async () => {
    if (selectedRows.length === 0) {
      message.warning('请选择要导入的记录');
      return;
    }
    
    setLoading(true);
    try {
      const billsToSave = selectedRows.map(index => parseResult.records[index]);
      const result = await billService.batchSaveBills(billsToSave);
      message.success(`成功导入 ${result.inserted} 条记录，跳过 ${result.skipped} 条`);
      handleClose();
      onSuccess();
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setParseResult(null);
    setSelectedRows([]);
    setSkippedRecords([]);
    setSelectedSkippedRows([]);
    onCancel();
  };

  const columns = [
    {
      title: '交易时间',
      dataIndex: 'transactionTime',
      key: 'transactionTime',
      width: 140,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '收/支',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 60,
      render: (type) => (
        <Tag color={type === 'income' ? 'green' : 'red'}>
          {type === 'income' ? '收入' : '支出'}
        </Tag>
      )
    },
    {
      title: '来源',
      dataIndex: 'sourcePlatform',
      key: 'sourcePlatform',
      width: 70,
      render: (platform) => {
        const platformMap = {
          wechat: { text: '微信', color: '#07c160' },
          alipay: { text: '支付宝', color: '#1677ff' }
        };
        const p = platformMap[platform] || { text: platform, color: '#8c8c8c' };
        return <Tag color={p.color}>{p.text}</Tag>;
      }
    },
    {
      title: '交易对方',
      dataIndex: 'counterparty',
      key: 'counterparty',
      ellipsis: true,
      width: 100
    },
    {
      title: '商品说明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 120
    },
    {
      title: '交易分类',
      dataIndex: 'originalType',
      key: 'originalType',
      width: 80,
      ellipsis: true,
      render: (type) => type || '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      align: 'right',
      render: (amount, record) => (
        <span style={{ color: record.transactionType === 'income' ? '#52c41a' : '#f5222d' }}>
          {record.transactionType === 'income' ? '+' : '-'}¥{(amount || 0).toFixed(2)}
        </span>
      )
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 90,
      ellipsis: true,
      render: (method) => method || '-'
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'transactionStatus',
      width: 80,
      render: (status) => {
        if (!status) return '-';
        const statusMap = {
          '交易成功': { color: 'green', text: '成功' },
          '已退款': { color: 'orange', text: '已退款' },
          '退款成功': { color: 'orange', text: '已退款' },
          '支付成功': { color: 'green', text: '成功' },
          '已关闭': { color: 'default', text: '已关闭' },
          '等待付款': { color: 'blue', text: '待付款' }
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      }
    },
    {
      title: '导入状态',
      key: 'importStatus',
      width: 70,
      render: (_, record, index) => {
        const duplicate = parseResult?.duplicates?.find(d => d.index === index);
        if (duplicate) {
          return <Tag color="orange">重复</Tag>;
        }
        if (record.amount === 0) {
          return <Tag color="default">金额为0</Tag>;
        }
        return <Tag color="green">新增</Tag>;
      }
    }
  ];

  const skippedColumns = [
    {
      title: '行号',
      dataIndex: 'row',
      key: 'row',
      width: 70
    },
    {
      title: '交易时间',
      dataIndex: ['rawData', 'transactionTime'],
      key: 'transactionTime',
      width: 150,
      render: (time) => time || '-'
    },
    {
      title: '交易对方',
      dataIndex: ['rawData', 'counterparty'],
      key: 'counterparty',
      width: 120,
      ellipsis: true,
      render: (val) => val || '-'
    },
    {
      title: '金额',
      dataIndex: ['rawData', 'amount'],
      key: 'amount',
      width: 100,
      render: (val) => val || '-'
    },
    {
      title: '跳过原因',
      dataIndex: 'message',
      key: 'message',
      render: (msg) => <span style={{ color: '#faad14' }}>{msg}</span>
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (keys) => setSelectedRows(keys),
    getCheckboxProps: (record) => {
      const index = parseResult?.records?.indexOf(record);
      const isDuplicate = parseResult?.duplicates?.some(d => d.index === index);
      // 只有重复记录禁用，金额为0的可以手动勾选
      return { disabled: isDuplicate };
    }
  };

  const skippedRowSelection = {
    selectedRowKeys: selectedSkippedRows,
    onChange: (keys) => setSelectedSkippedRows(keys)
  };

  // 统计金额为0的记录数
  const zeroAmountCount = parseResult?.records?.filter(r => r.amount === 0).length || 0;

  const tabItems = [
    {
      key: 'valid',
      label: `有效记录 (${parseResult?.records?.length || 0})`,
      children: (
        <>
          {zeroAmountCount > 0 && (
            <Alert
              message={`有 ${zeroAmountCount} 条金额为0的记录默认未勾选，如需导入请手动勾选`}
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={parseResult?.records?.map((r, i) => ({ ...r, key: i })) || []}
            size="small"
            scroll={{ y: zeroAmountCount > 0 ? 310 : 350 }}
            pagination={false}
          />
        </>
      )
    },
    {
      key: 'skipped',
      label: `跳过记录 (${skippedRecords.length})`,
      children: skippedRecords.length > 0 ? (
        <Table
          rowSelection={skippedRowSelection}
          columns={skippedColumns}
          dataSource={skippedRecords.map((r, i) => ({ ...r, key: i }))}
          size="small"
          scroll={{ y: 350 }}
          pagination={false}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          没有跳过的记录
        </div>
      )
    }
  ];

  return (
    <Modal
      title="导入账单"
      open={visible}
      onCancel={handleClose}
      width={1100}
      footer={step === 1 ? null : [
        <Button key="cancel" onClick={handleClose}>取消</Button>,
        <Button key="back" onClick={() => setStep(1)}>重新上传</Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          导入选中 ({selectedRows.length})
        </Button>
      ]}
    >
      {step === 1 && (
        <Dragger
          accept=".xlsx,.xls,.csv"
          beforeUpload={handleUpload}
          showUploadList={false}
          disabled={loading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">
            支持微信账单（.xlsx）和支付宝账单（.csv）
          </p>
        </Dragger>
      )}
      
      {step === 2 && parseResult && (
        <>
          <Alert
            message={
              <Space>
                <span>解析完成：共 {parseResult.total} 条有效记录</span>
                {parseResult.skipped > 0 && <span>，跳过 {parseResult.skipped} 条无效记录</span>}
                {parseResult.duplicates?.length > 0 && (
                  <span>，检测到 {parseResult.duplicates.length} 条重复记录</span>
                )}
                <Tag color={parseResult.platform === 'wechat' ? 'green' : 'blue'}>
                  {parseResult.platform === 'wechat' ? '微信' : '支付宝'}
                </Tag>
              </Space>
            }
            type="info"
            style={{ marginBottom: 16 }}
          />
          <Tabs items={tabItems} />
        </>
      )}
    </Modal>
  );
};

export default ImportModal;
